/**
 * Head pose (yaw / pitch) from MediaPipe Face Mesh landmarks.
 * Uses the same 468-point mesh WebGazer already runs on-device.
 *
 * Landmark indices (image coordinates; x/y often pixels, z much smaller):
 *   1   nose tip
 *   10  forehead / top of face
 *   152 chin
 *   234 left cheek (subject's left)
 *   454 right cheek
 *   33 / 133  left eye outer / inner
 *   263 / 362 right eye outer / inner
 *
 * Important: nose-vs-cheek-midpoint alone underestimates turns — MediaPipe's
 * mesh moves rigidly, so the nose stays near the cheek midpoint. We therefore
 * also use eye-width asymmetry and nose–cheek distance ratio.
 */

export const HEAD_YAW_AWAY_DEG = 22;
export const HEAD_PITCH_AWAY_DEG = 18;

/** Eye width ratio (narrower/wider) below this ⇒ strong profile. */
export const HEAD_EYE_PROFILE_RATIO = 0.45;

/** WebGazer eye-patch width ratio (slightly looser — patches are coarse boxes). */
export const HEAD_EYE_PATCH_PROFILE_RATIO = 0.55;

export type HeadPoseDegrees = {
  yaw: number;
  pitch: number;
  /** True when one eye is much narrower than the other (side profile). */
  profileByEyes?: boolean;
};

/**
 * True when WebGazer left/right eye-patch widths indicate a side profile.
 * This is the live signal we get every frame even when landmark yaw is weak.
 */
export function isEyePatchProfile(
  leftWidth: number,
  rightWidth: number,
  ratioThreshold: number = HEAD_EYE_PATCH_PROFILE_RATIO
): boolean {
  if (!Number.isFinite(leftWidth) || !Number.isFinite(rightWidth)) return false;
  const narrow = Math.min(leftWidth, rightWidth);
  const wide = Math.max(leftWidth, rightWidth);
  if (wide < 2) return false;
  return narrow / wide < ratioThreshold;
}

type Landmark = ReadonlyArray<number>;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function dist2(a: Landmark, b: Landmark): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/**
 * Estimate head yaw/pitch in degrees from Face Mesh positions.
 * yaw > 0 ≈ nose toward image-left (often subject turning right on mirrored webcam)
 * pitch > 0 ≈ nose below face midline (chin tucked / looking down)
 */
export function estimateHeadPoseDegrees(
  positions: ReadonlyArray<Landmark> | null | undefined
): HeadPoseDegrees | null {
  if (!positions || positions.length < 455) return null;

  const nose = positions[1];
  const forehead = positions[10];
  const chin = positions[152];
  const leftCheek = positions[234];
  const rightCheek = positions[454];
  if (!nose || !forehead || !chin || !leftCheek || !rightCheek) return null;
  if (nose.length < 2 || leftCheek.length < 2 || rightCheek.length < 2) return null;

  const midX = (leftCheek[0] + rightCheek[0]) / 2;
  const faceWidth =
    Math.hypot(rightCheek[0] - leftCheek[0], rightCheek[1] - leftCheek[1]) || 1;

  // 2D nose offset relative to cheeks (weak under rigid mesh motion)
  const yawFromX =
    Math.asin(clamp((nose[0] - midX) / (faceWidth * 0.45), -1, 1)) *
    (180 / Math.PI);

  // Nose–cheek distance asymmetry (stronger under yaw)
  const dLeft = dist2(nose, leftCheek);
  const dRight = dist2(nose, rightCheek);
  const yawFromDist =
    ((dLeft - dRight) / (dLeft + dRight + 1e-6)) * 90;

  // Cheek depth: rescale tiny MediaPipe z into face-width units before atan2
  let yawFromZ = 0;
  if (leftCheek.length > 2 && rightCheek.length > 2) {
    const zL = leftCheek[2] ?? 0;
    const zR = rightCheek[2] ?? 0;
    const zSpan = Math.abs(zL - zR);
    // If z is already pixel-scale, use as-is; if tiny, amplify to faceWidth band.
    const zScale = zSpan > faceWidth * 0.05 ? 1 : faceWidth / Math.max(zSpan, 1e-4);
    yawFromZ = Math.atan2((zL - zR) * zScale, faceWidth) * (180 / Math.PI);
  }

  // Eye-line: nose vs outer-eye midpoint
  let yawFromEyes = 0;
  let profileByEyes = false;
  const leftOuter = positions[33];
  const leftInner = positions[133];
  const rightOuter = positions[263];
  const rightInner = positions[362];
  if (leftOuter && rightOuter && leftOuter.length >= 2 && rightOuter.length >= 2) {
    const eyeMidX = (leftOuter[0] + rightOuter[0]) / 2;
    const iod = dist2(leftOuter, rightOuter);
    // Ignore unset / garbage eye points (mesh slots default to 0,0 in tests & cold start).
    const eyesPlausible =
      iod > faceWidth * 0.15 &&
      Math.abs(eyeMidX - midX) < faceWidth * 1.5;

    if (eyesPlausible) {
      yawFromEyes =
        Math.asin(clamp((nose[0] - eyeMidX) / (iod * 0.55), -1, 1)) *
        (180 / Math.PI);

      if (leftInner && rightInner && leftInner.length >= 2 && rightInner.length >= 2) {
        const leftW = dist2(leftOuter, leftInner);
        const rightW = dist2(rightOuter, rightInner);
        const narrow = Math.min(leftW, rightW);
        const wide = Math.max(leftW, rightW);
        if (wide > faceWidth * 0.02 && narrow / wide < HEAD_EYE_PROFILE_RATIO) {
          profileByEyes = true;
          const sign = leftW < rightW ? -1 : 1;
          yawFromEyes = sign * Math.max(Math.abs(yawFromEyes), HEAD_YAW_AWAY_DEG + 8);
        }
      }
    }
  }

  const yawCandidates = [yawFromX, yawFromDist, yawFromZ, yawFromEyes];
  let yaw = yawCandidates.reduce((best, v) =>
    Math.abs(v) > Math.abs(best) ? v : best
  , 0);

  if (profileByEyes && Math.abs(yaw) < HEAD_YAW_AWAY_DEG) {
    yaw = Math.sign(yawFromEyes || yaw || 1) * (HEAD_YAW_AWAY_DEG + 8);
  }

  const midY = (forehead[1] + chin[1]) / 2;
  const faceHeight =
    Math.hypot(chin[0] - forehead[0], chin[1] - forehead[1]) || 1;
  const pitch =
    Math.asin(clamp((nose[1] - midY) / (faceHeight * 0.45), -1, 1)) *
    (180 / Math.PI);

  if (!Number.isFinite(yaw) || !Number.isFinite(pitch)) return null;
  return { yaw, pitch, profileByEyes };
}

/** True when absolute yaw/pitch exceed away-from-camera thresholds. */
export function isHeadPoseAwayFromCamera(pose: HeadPoseDegrees | null | undefined): boolean {
  if (!pose) return false;
  if (pose.profileByEyes) return true;
  return (
    Math.abs(pose.yaw) >= HEAD_YAW_AWAY_DEG ||
    Math.abs(pose.pitch) >= HEAD_PITCH_AWAY_DEG
  );
}
